import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProjects, GET_PROJECT_ROLES } from "@/hooks/useProjects";
import { useSchedulingChangesProjectIds } from "@/hooks/useSchedulingSync";
import { useColumnTasks } from "@/hooks/useColumnTasks";
import { useUpcomingProjectTasks } from "@/hooks/useUpcomingProjectTasks";
import { useVisibleRowsHeight } from "@/hooks/useVisibleRowsHeight";
import { useTaskMutations } from "@/hooks/useTasks";
import { useTaskCompletionToggle } from "@/hooks/useTaskCompletionToggle";
import { useRecentTaskStatusEvents } from "@/hooks/useTaskStatusEvents";
import { useUsers } from "@/hooks/useUsers";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { WeekStrip } from "@/components/dashboard/WeekStrip";
import {
  ProjectHealthCard,
  type ProjectHealth,
} from "@/components/dashboard/ProjectHealthCard";
import { ActivityFeedRow } from "@/components/dashboard/ActivityFeedRow";
import { TaskSummaryCard } from "@/components/tasks/TaskSummaryCard";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { getCurrentIsoWeek } from "@/lib/weekUtils";
import { cn } from "@/lib/utils";
import { HomeIcon } from "@/lib/icons";
import { ProjectStatus, TaskStatus } from "@/types";
import type { ProjectWithPlots, TaskExtended } from "@/types";

const LIST_CAP = 25;

// Rows visible before each bottom-row list starts scrolling.
const VISIBLE_ROWS = 5;

// Past this many hankkeet the grid switches from wrapping rows to two rows
// that scroll sideways, so the cards never push the lists below them off
// screen. Six fills two rows at the widest breakpoint.
const PROJECT_SCROLL_THRESHOLD = 6;

/** Statuses that count as unfinished — they can be overdue or fall due. */
const NOT_DONE: TaskStatus[] = [
  TaskStatus.PENDING,
  TaskStatus.IN_PROGRESS,
  TaskStatus.REJECTED,
  TaskStatus.CANNOT_MEASURE,
];

const HIDDEN_PROJECT_STATUSES: string[] = [
  ProjectStatus.ARCHIVED,
  ProjectStatus.LOST,
];

type Aggregate = {
  total: number;
  completed: number;
  inProgress: number;
  late: number;
};

function aggregateProject(project: ProjectWithPlots): Aggregate {
  const a: Aggregate = { total: 0, completed: 0, inProgress: 0, late: 0 };
  for (const plot of project.plots ?? []) {
    for (const hs of plot.headsectionStats ?? []) {
      a.total += hs.total;
      a.completed += hs.completed;
      a.inProgress += hs.inProgress;
      a.late += hs.late;
    }
  }
  return a;
}

// Local-time boundaries. Overdue = strictly before today; "this week" runs from
// today through Sunday (so it never overlaps the overdue set); completed count
// runs from Monday 00:00.
function boundaries() {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const overdueBefore = new Date(startOfToday.getTime() - 1);
  const dow = (startOfToday.getDay() + 6) % 7;
  const weekStart = new Date(startOfToday);
  weekStart.setDate(startOfToday.getDate() - dow);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);
  weekEnd.setMilliseconds(-1);
  return {
    startOfTodayISO: startOfToday.toISOString(),
    overdueBeforeISO: overdueBefore.toISOString(),
    weekStartISO: weekStart.toISOString(),
    weekEndISO: weekEnd.toISOString(),
  };
}

const DATE_FORMAT = new Intl.DateTimeFormat("fi-FI", {
  weekday: "long",
  day: "numeric",
  month: "numeric",
  year: "numeric",
});

export function Etusivu() {
  const { user } = useAuth();
  const { projects, loading: projectsLoading } = useProjects();
  const { users } = useUsers();
  const { projectIdSet: schedulingChangeProjectIds } =
    useSchedulingChangesProjectIds();
  const { updateTaskStatus } = useTaskMutations();
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskExtended | null>(null);

  const firstName = (user?.name ?? "").split(" ")[0] || "Käyttäjä";
  const today = useMemo(() => DATE_FORMAT.format(new Date()), []);
  const week = useMemo(() => getCurrentIsoWeek().week, []);

  const currentUserId = useMemo(() => {
    if (!user?.id) return "";
    return users.find((u) => u.azureObjectId === user.id)?.id || "";
  }, [user, users]);

  const { data: projectRolesData } = useQuery<{
    projectRoles: { items: Array<{ projectId: string }> };
  }>(GET_PROJECT_ROLES, {
    variables: { filter: { userId: currentUserId }, limit: 100 },
    skip: !currentUserId,
    fetchPolicy: "cache-and-network",
  });

  const myProjectIds = useMemo(
    () => (projectRolesData?.projectRoles?.items ?? []).map((r) => r.projectId),
    [projectRolesData],
  );

  const { startOfTodayISO, overdueBeforeISO, weekStartISO, weekEndISO } =
    useMemo(boundaries, []);

  const completed = useColumnTasks(
    TaskStatus.COMPLETED,
    { completedAfter: weekStartISO, sortBy: "deadline", sortDirection: "desc" },
    false,
    [TaskStatus.COMPLETED],
  );
  const overdue = useColumnTasks(
    TaskStatus.PENDING,
    {
      responsibleUserIds: currentUserId ? [currentUserId] : [],
      dueBefore: overdueBeforeISO,
      sortBy: "deadline",
      sortDirection: "asc",
    },
    !currentUserId,
    NOT_DONE,
  );
  const thisWeek = useColumnTasks(
    TaskStatus.PENDING,
    {
      responsibleUserIds: currentUserId ? [currentUserId] : [],
      dueAfter: startOfTodayISO,
      dueBefore: weekEndISO,
      sortBy: "deadline",
      sortDirection: "asc",
    },
    !currentUserId,
    NOT_DONE,
  );
  const waiting = useColumnTasks(
    TaskStatus.PROBLEM,
    { sortBy: "deadline", sortDirection: "asc" },
    false,
    [TaskStatus.PROBLEM],
  );

  const { events: recentActivity, refetch: refetchRecent } =
    useRecentTaskStatusEvents(myProjectIds, 100);

  const activityTop = useMemo(
    () => recentActivity.filter((e) => !e.task?.parentId).slice(0, LIST_CAP),
    [recentActivity],
  );

  const overdueTop = overdue.tasks.slice(0, LIST_CAP);

  // The myöhässä list and the valmistuneet count both shift when a card is
  // toggled, and the toggle writes a status event the activity feed shows.
  const refetchAfterCompletionToggle = useCallback(() => {
    overdue.refetch();
    completed.refetch();
    refetchRecent();
  }, [overdue.refetch, completed.refetch, refetchRecent]);
  const toggleTaskCompleted = useTaskCompletionToggle(
    refetchAfterCompletionToggle,
  );

  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects) m.set(p.id, p.name);
    return m;
  }, [projects]);

  const plotNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects) {
      for (const plot of p.plots ?? []) m.set(plot.id, plot.name);
    }
    return m;
  }, [projects]);

  const visibleProjects = useMemo(
    () => projects.filter((p) => !HIDDEN_PROJECT_STATUSES.includes(p.status)),
    [projects],
  );

  const visibleProjectIds = useMemo(
    () => visibleProjects.map((p) => p.id),
    [visibleProjects],
  );

  const { byProject: upcoming } = useUpcomingProjectTasks(
    visibleProjectIds,
    startOfTodayISO,
    weekEndISO,
  );

  // Trouble first: most myöhässä, then least complete.
  const projectHealth = useMemo<ProjectHealth[]>(() => {
    return visibleProjects
      .map((project) => {
        const agg = aggregateProject(project);
        const up = upcoming.get(project.id);
        return {
          id: project.id,
          name: project.name,
          status: project.status,
          ...agg,
          dueThisWeek: up?.dueThisWeek ?? 0,
          next: up?.next ?? null,
        };
      })
      .sort((a, b) => {
        if (b.late !== a.late) return b.late - a.late;
        const ratio = (h: ProjectHealth) => (h.total ? h.completed / h.total : 1);
        return ratio(a) - ratio(b);
      });
  }, [visibleProjects, upcoming]);

  const scrollProjects = projectHealth.length > PROJECT_SCROLL_THRESHOLD;
  const overdueScroll = useVisibleRowsHeight(VISIBLE_ROWS, overdueTop.length);
  const activityScroll = useVisibleRowsHeight(VISIBLE_ROWS, activityTop.length);

  const openTaskDialog = useCallback((task: TaskExtended) => {
    setSelectedTask(task);
    setDialogOpen(true);
  }, []);

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      setDialogOpen(open);
      if (!open) refetchRecent();
    },
    [refetchRecent],
  );

  const handleUpdateStatus = async (taskId: string, status: TaskStatus) => {
    try {
      if (selectedTask?.id === taskId && selectedTask.version != null) {
        await updateTaskStatus(taskId, status, selectedTask.version);
      }
      handleDialogOpenChange(false);
    } catch (error) {
      console.error("Failed to update task status:", error);
    }
  };

  if (projectsLoading) {
    return (
      <div>
        <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 border-b bg-background px-4 py-3 shrink-0 -mx-6 -mt-6">
          <Skeleton className="h-6 w-32" />
        </div>
        <div className="flex flex-col gap-6 pt-6">
          <Skeleton className="h-[104px]" />
          <div className="grid gap-5 md:grid-cols-2 2xl:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[228px]" />
            ))}
          </div>
          <div className="grid gap-6 lg:grid-cols-5">
            <Skeleton className="h-80 lg:col-span-3" />
            <Skeleton className="h-80 lg:col-span-2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 border-b bg-background px-4 py-3 shrink-0 -mx-6 -mt-6">
        <h1 className="mr-2 flex items-center gap-2 text-lg font-semibold">
          <HomeIcon className="h-5 w-5" />
          Etusivu
        </h1>
        <span className="text-sm text-muted-foreground">
          Tervetuloa takaisin, {firstName}
        </span>
        <span className="ml-auto text-sm text-muted-foreground">
          {today} · viikko {week}
        </span>
      </div>

      <div className="flex flex-col gap-6 pt-6">

        <WeekStrip
          stats={[
            { label: "Määräaikaa tällä viikolla", value: thisWeek.totalCount },
            {
              label: "Myöhässä vastuullasi",
              value: overdue.totalCount,
              tone: "crit",
            },
            { label: "Odottaa aliurakoitsijaa", value: waiting.totalCount },
            { label: "Valmistunut tällä viikolla", value: completed.totalCount },
          ]}
        />

        <section className="flex flex-col gap-3">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Hankkeiden tilanne
          </h2>
          {projectHealth.length === 0 ? (
            <Card className="p-6 text-sm text-muted-foreground">
              Ei hankkeita. Lisää hanke Hankkeet-sivulta.
            </Card>
          ) : (
            <div
              className={cn(
                scrollProjects
                  ? "-mx-1 overflow-x-auto px-1 pb-2"
                  : undefined,
              )}
            >
              <div
                className={cn(
                  "grid gap-5",
                  scrollProjects
                    ? "grid-flow-col grid-rows-2 auto-cols-[20rem]"
                    : "md:grid-cols-2 2xl:grid-cols-3",
                )}
              >
                {projectHealth.map((health) => (
                  <ProjectHealthCard
                    key={health.id}
                    health={health}
                    hasSchedulingChanges={schedulingChangeProjectIds.has(health.id)}
                    onClick={() => navigate("/projects")}
                  />
                ))}
              </div>
            </div>
          )}
        </section>

        <div className="grid gap-6 lg:grid-cols-5">
          <section className="flex flex-col gap-3 lg:col-span-3">
            <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Vaatii huomiotasi
            </h2>
            {overdueTop.length === 0 ? (
              <Card className="p-6 text-sm text-muted-foreground">
                Ei myöhässä olevia tehtäviä.
              </Card>
            ) : (
              <div
                ref={overdueScroll.ref}
                className="-mx-1 flex flex-col gap-2.5 overflow-y-auto px-1"
                style={{ maxHeight: overdueScroll.maxHeight }}
              >
                {overdueTop.map((task) => (
                  <TaskSummaryCard
                    key={task.id}
                    task={task}
                    projectNameById={projectNameById}
                    plotNameById={plotNameById}
                    onClick={() => openTaskDialog(task)}
                    onToggleCompleted={toggleTaskCompleted}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="flex flex-col gap-3 lg:col-span-2">
            <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Viimeisin toiminta
            </h2>
            {activityTop.length === 0 ? (
              <Card className="p-6 text-sm text-muted-foreground">Ei toimintaa.</Card>
            ) : (
              <Card className="px-3">
                <div
                  ref={activityScroll.ref}
                  className="divide-y divide-border overflow-y-auto"
                  style={{ maxHeight: activityScroll.maxHeight }}
                >
                  {activityTop.map((event) => (
                    <ActivityFeedRow
                      key={event.id}
                      event={event}
                      onClick={openTaskDialog}
                    />
                  ))}
                </div>
              </Card>
            )}
          </section>
        </div>
      </div>

      <TaskDialog
        task={selectedTask}
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        onUpdateStatus={handleUpdateStatus}
        currentUserId={currentUserId}
        onTaskUpdated={setSelectedTask}
        onSubtasksChanged={refetchAfterCompletionToggle}
      />
    </div>
  );
}
